import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { logger } from "../../utils/logger";
import {
  DEPARTAMENTOS_SIN_LIMPIEZA,
  type MotivoDiaSinPersonal,
} from "./cleaningConfig";

dayjs.extend(isSameOrBefore);

export function esDepartamentoExcluidoDeLimpieza(departamento?: string | null): boolean {
  return DEPARTAMENTOS_SIN_LIMPIEZA.includes((departamento || "").trim().toLowerCase());
}

export type GetMotivoDiaSinPersonal = (fecha: string) => MotivoDiaSinPersonal;

function motivoDomingo(fecha: string): MotivoDiaSinPersonal {
  return dayjs(fecha).day() === 0 ? "domingo" : null;
}

function ajustarDiaConPersonal(
  fecha: dayjs.Dayjs,
  getMotivoDiaSinPersonal: GetMotivoDiaSinPersonal
): {
  fecha: dayjs.Dayjs;
  fechaOriginal?: string;
  motivo?: Exclude<MotivoDiaSinPersonal, null>;
} {
  const fechaOriginal = fecha.format("YYYY-MM-DD");
  const motivoOriginal = getMotivoDiaSinPersonal(fechaOriginal);
  if (!motivoOriginal) return { fecha };

  let fechaAjustada = fecha.add(1, "day");
  let guard = 0;
  while (getMotivoDiaSinPersonal(fechaAjustada.format("YYYY-MM-DD")) && guard < 14) {
    fechaAjustada = fechaAjustada.add(1, "day");
    guard += 1;
  }

  return {
    fecha: fechaAjustada,
    fechaOriginal,
    motivo: motivoOriginal,
  };
}

export type TipoLimpieza = "checkout" | "intermedia" | "manual";

export interface LimpiezaCalculada {
  fecha: string;
  tipo: TipoLimpieza;
  fecha_original?: string;
  motivo_reprogramacion?: Exclude<MotivoDiaSinPersonal, null>;
  /**
   * Solo aplica a limpiezas pactadas (tipo "manual"): la fecha pactada cae en un día
   * sin personal pero NO se mueve (decisión humana). Es una advertencia, no una reprogramación.
   */
  advertencia_sin_personal?: Exclude<MotivoDiaSinPersonal, null>;
}

export interface CalcularEventosLimpiezaOptions {
  getMotivoDiaSinPersonal?: GetMotivoDiaSinPersonal;
  fechaReferencia?: string;
  sinLimpiezaAutomatica?: boolean;
  /**
   * Fechas en las que el personal registró una limpieza de esta reserva. Una
   * automática pasada normalmente se oculta cuando hay pactadas (es plan viejo),
   * pero si se hizo, es un hecho y se muestra.
   */
  fechasConRegistro?: string[];
}

interface ReservaConContinuidad {
  id: number;
  departamento?: string;
  entrada?: string;
  salida?: string;
  nombre?: string;
  apellido?: string;
  dni?: string;
  cliente?: { id?: number };
}

function normalizarTextoComparacion(value?: string): string {
  return (value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

export function mismoHuesped(a?: ReservaConContinuidad, b?: ReservaConContinuidad): boolean {
  if (!a || !b) return false;
  if (a.cliente?.id && b.cliente?.id && a.cliente.id === b.cliente.id) return true;
  const dniA = (a.dni || "").replace(/\D/g, "");
  const dniB = (b.dni || "").replace(/\D/g, "");
  if (dniA && dniB && dniA === dniB) return true;
  const nombreA = normalizarTextoComparacion(a.nombre);
  const nombreB = normalizarTextoComparacion(b.nombre);
  if (!nombreA || !nombreB || nombreA !== nombreB) return false;
  return normalizarTextoComparacion(a.apellido) === normalizarTextoComparacion(b.apellido);
}

export function tieneContinuidadMismoHuesped(
  reserva: ReservaConContinuidad,
  reservas: ReservaConContinuidad[]
): boolean {
  if (!reserva.departamento || !reserva.salida) return false;
  return reservas.some((entrada) =>
    entrada.id !== reserva.id
    && entrada.departamento === reserva.departamento
    && entrada.entrada === reserva.salida
    && mismoHuesped(reserva, entrada)
  );
}

function prioridadTipoLimpieza(tipo: TipoLimpieza): number {
  if (tipo === "checkout") return 3;
  if (tipo === "manual") return 2;
  return 1;
}

function agregarLimpieza(
  limpiezas: Map<string, LimpiezaCalculada>,
  fecha: string,
  tipo: TipoLimpieza,
  fechaOriginal?: string,
  motivoReprogramacion?: Exclude<MotivoDiaSinPersonal, null>,
  advertenciaSinPersonal?: Exclude<MotivoDiaSinPersonal, null>
) {
  const existente = limpiezas.get(fecha);
  if (!existente || prioridadTipoLimpieza(tipo) > prioridadTipoLimpieza(existente.tipo)) {
    limpiezas.set(fecha, {
      fecha,
      tipo,
      fecha_original: fechaOriginal,
      motivo_reprogramacion: motivoReprogramacion,
      advertencia_sin_personal: advertenciaSinPersonal,
    });
  }
}

export function calcularEventosLimpieza(
  entrada: string,
  salida: string,
  dias_limpieza_pactados?: string[] | null,
  options: CalcularEventosLimpiezaOptions = {}
): LimpiezaCalculada[] {
  const start = dayjs(entrada);
  const end = dayjs(salida);

  if (!start.isValid() || !end.isValid()) {
    logger.warn(
      `[limpiezaUtils] Fechas inválidas recibidas: ${entrada}, ${salida}`
    );
    return [];
  }

  const noches = end.diff(start, "day");
  logger.debug(`[limpiezaUtils] Calculando limpieza para ${noches} noches.`);

  const getMotivoDiaSinPersonal = options.getMotivoDiaSinPersonal || motivoDomingo;
  const fechaReferencia = options.fechaReferencia ? dayjs(options.fechaReferencia) : null;
  const limpiezas = new Map<string, LimpiezaCalculada>();
  const fechasPactadas: dayjs.Dayjs[] = [];
  const checkout = ajustarDiaConPersonal(end, getMotivoDiaSinPersonal);
  agregarLimpieza(
    limpiezas,
    checkout.fecha.format("YYYY-MM-DD"),
    "checkout",
    checkout.fechaOriginal,
    checkout.motivo
  );

  const tieneLimpiezasPactadas = Boolean(dias_limpieza_pactados && dias_limpieza_pactados.length > 0);

  // Las pactadas son una decisión humana: se suman a las automáticas y nunca se mueven.
  if (tieneLimpiezasPactadas) {
    logger.debug(
      `[limpiezaUtils] Agregando limpiezas pactadas: ${dias_limpieza_pactados?.length || 0} fechas, manteniendo automáticas y checkout`
    );

    for (const fechaStr of dias_limpieza_pactados || []) {
      const fecha = dayjs(fechaStr);
      if (!fecha.isValid()) {
        logger.warn(`[limpiezaUtils] Fecha inválida en limpiezas pactadas: ${fechaStr}`);
        continue;
      }

      if (fecha.diff(start, "day") >= 0 && fecha.diff(end, "day") <= 0) {
        // Las pactadas son una decisión humana: nunca se mueven, aunque caigan en un
        // día sin personal. Solo se marcan con una advertencia para que el operador
        // confirme la cobertura antes de ir.
        const fechaFormateada = fecha.format("YYYY-MM-DD");
        const motivoSinPersonal = getMotivoDiaSinPersonal(fechaFormateada);
        fechasPactadas.push(fecha);
        agregarLimpieza(
          limpiezas,
          fechaFormateada,
          "manual",
          undefined,
          undefined,
          motivoSinPersonal || undefined
        );
        if (motivoSinPersonal) {
          logger.warn(
            `[limpiezaUtils] Limpieza pactada ${fechaStr} cae en un día sin personal (${motivoSinPersonal}); se respeta la fecha pactada.`
          );
        } else {
          logger.debug(`[limpiezaUtils] Limpieza pactada agregada: ${fechaStr}`);
        }
      } else {
        logger.warn(
          `[limpiezaUtils] Fecha de limpieza fuera de rango: ${fechaStr} (reserva: ${entrada} a ${salida})`
        );
      }
    }
  }

  if (options.sinLimpiezaAutomatica) {
    return Array.from(limpiezas.values());
  }

  const ultimoDiaPosible = end.subtract(1, "day");
  const esAutomaticaFutura = (fecha: dayjs.Dayjs) =>
    !fechaReferencia || !fecha.isBefore(fechaReferencia, "day");
  const hayPactadasHastaReferencia = Boolean(
    fechaReferencia && fechasPactadas.some((fechaPactada) => fechaPactada.isSameOrBefore(fechaReferencia, "day"))
  );
  const fechasConRegistro = new Set(options.fechasConRegistro || []);
  const ocultarAutomaticaPasadaPorPactadas = (fecha: dayjs.Dayjs) =>
    hayPactadasHastaReferencia
    && fecha.isBefore(fechaReferencia!, "day")
    && !fechasConRegistro.has(fecha.format("YYYY-MM-DD"));
  const estaCercaDePactada = (fecha: dayjs.Dayjs) =>
    fechasPactadas.some((fechaPactada) => Math.abs(fecha.diff(fechaPactada, "day")) < 3);
  const agregarIntermediasCada = (intervalo: number, diasMinimosAntesCheckout = 0) => {
    let ultimaIntermedia: dayjs.Dayjs | null = null;
    let proximaFechaLimpieza = start.add(intervalo, "day");
    while (proximaFechaLimpieza.isSameOrBefore(ultimoDiaPosible)) {
      const fechaOperativa = ajustarDiaConPersonal(proximaFechaLimpieza, getMotivoDiaSinPersonal);
      if (ultimaIntermedia && fechaOperativa.fecha.diff(ultimaIntermedia, "day") < 3) {
        let fechaSeparada = ultimaIntermedia.add(3, "day");
        while (getMotivoDiaSinPersonal(fechaSeparada.format("YYYY-MM-DD"))) {
          fechaSeparada = fechaSeparada.add(1, "day");
        }
        fechaOperativa.fecha = fechaSeparada;
      }
      if (
        diasMinimosAntesCheckout > 0
        && end.diff(fechaOperativa.fecha, "day") < diasMinimosAntesCheckout
      ) {
        proximaFechaLimpieza = proximaFechaLimpieza.add(intervalo, "day");
        continue;
      }
      if (esAutomaticaFutura(fechaOperativa.fecha) && estaCercaDePactada(fechaOperativa.fecha)) {
        proximaFechaLimpieza = proximaFechaLimpieza.add(intervalo, "day");
        continue;
      }
      if (ocultarAutomaticaPasadaPorPactadas(fechaOperativa.fecha)) {
        proximaFechaLimpieza = proximaFechaLimpieza.add(intervalo, "day");
        continue;
      }
      agregarLimpieza(
        limpiezas,
        fechaOperativa.fecha.format("YYYY-MM-DD"),
        "intermedia",
        fechaOperativa.fechaOriginal,
        fechaOperativa.motivo
      );
      ultimaIntermedia = fechaOperativa.fecha;
      proximaFechaLimpieza = proximaFechaLimpieza.add(intervalo, "day");
    }
  };

  if (noches >= 21) {
    agregarIntermediasCada(4, 3);
  } else if (noches >= 11) {
    agregarIntermediasCada(4, 3);
  } else if (noches >= 8) {
    agregarIntermediasCada(3, 3);
  } else if (noches >= 5) {
    const fechaDia3 = ajustarDiaConPersonal(start.add(3, "day"), getMotivoDiaSinPersonal);
    if (
      !ocultarAutomaticaPasadaPorPactadas(fechaDia3.fecha)
      && (!esAutomaticaFutura(fechaDia3.fecha) || !estaCercaDePactada(fechaDia3.fecha))
    ) {
      agregarLimpieza(
        limpiezas,
        fechaDia3.fecha.format("YYYY-MM-DD"),
        "intermedia",
        fechaDia3.fechaOriginal,
        fechaDia3.motivo
      );
    }
  }

  return Array.from(limpiezas.values());
}

export function calcularDiasLimpieza(
  entrada: string,
  salida: string,
  dias_limpieza_pactados?: string[] | null,
  options: CalcularEventosLimpiezaOptions = {}
): string[] {
  return calcularEventosLimpieza(entrada, salida, dias_limpieza_pactados, options).map(
    (limpieza) => limpieza.fecha
  );
}

export function esLimpiezaVisibleEnCalendarioReserva(
  limpieza: LimpiezaCalculada,
  salida: string,
  permitirPosteriorPorContinuidad = false
): boolean {
  const fecha = dayjs(limpieza.fecha);
  const fechaSalida = dayjs(salida);
  if (!fecha.isValid() || !fechaSalida.isValid()) return false;

  // Un checkout corrido a un día con personal se dibuja en su día de salida
  // (nunca en el día al que se corrió: ahí pintaría sobre la reserva entrante).
  // Así el calendario también muestra los que hace el responsable en domingo.
  if (limpieza.tipo === "checkout" && limpieza.motivo_reprogramacion) {
    return Boolean(limpieza.fecha_original);
  }
  if (fecha.isAfter(fechaSalida, "day")) {
    return Boolean(
      permitirPosteriorPorContinuidad
      && limpieza.tipo === "intermedia"
      && limpieza.motivo_reprogramacion
      && limpieza.fecha_original === salida
    );
  }

  return true;
}

export function fechaLimpiezaCalendarioReserva(
  limpieza: LimpiezaCalculada,
  salida: string,
  permitirPosteriorPorContinuidad = false
): string | null {
  if (!esLimpiezaVisibleEnCalendarioReserva(limpieza, salida, permitirPosteriorPorContinuidad)) {
    return null;
  }

  if (limpieza.tipo === "checkout" && limpieza.motivo_reprogramacion) {
    return limpieza.fecha_original || limpieza.fecha;
  }

  return limpieza.fecha;
}
